/**
 * Twinkle Cart — shared utilities
 * @module cart-utils
 * Depends on: global.js (fetchConfig), theme.liquid routes
 */
window.TwinkleCart = window.TwinkleCart || {};

TwinkleCart.getSectionInnerHTML = function (html, selector = '.shopify-section') {
  if (!html) return '';
  const node = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  return node ? node.innerHTML : '';
};

TwinkleCart.getDrawer = function () {
  return document.querySelector('cart-drawer');
};

TwinkleCart.getDrawerSectionIds = function () {
  const drawer = TwinkleCart.getDrawer();
  return drawer ? drawer.getSectionsToRender().map((section) => section.id) : [];
};

TwinkleCart.getDrawerSectionPayload = function () {
  return {
    sections: TwinkleCart.getDrawerSectionIds(),
    section_url: window.location.pathname,
  };
};

TwinkleCart.renderDrawerResponse = function (response, { openDrawer = true } = {}) {
  const drawer = TwinkleCart.getDrawer();
  if (!drawer || !response) return;
  drawer.classList.toggle('is-empty', response.item_count === 0);
  drawer.renderContents(response, { openDrawer });
};

TwinkleCart.dispatchCartUpdate = function (cart) {
  document.dispatchEvent(new CustomEvent('cart:update', { detail: { cart } }));
};

TwinkleCart.applyDiscountCode = async function (code) {
  if (!code) return { cart: null, applied: false };

  const root = window.Shopify?.routes?.root || '/';
  const payload = {
    discount: code,
    ...TwinkleCart.getDrawerSectionPayload()
  };

  const response = await fetch(`${root}cart/update.js`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest'
    },
    body: JSON.stringify(payload)
  }).then((res) => res.json());

  const applied = (response.cart_level_discount_applications || []).some(
    (discount) => discount.title.toUpperCase() === code.toUpperCase()
  );

  if (applied) {
    if (response.sections && TwinkleCart.getDrawer()) {
      TwinkleCart.renderDrawerResponse(response, { openDrawer: false });
    }
    TwinkleCart.dispatchCartUpdate(response);
  }

  return { cart: response, applied };
};

TwinkleCart.syncEditStateFromCart = function (cart) {
  const carthandle = window.location.pathname.match(/\/products\/([^/?#]+)/)?.[1];
  if (!carthandle) return;

  const clearEditState = () => {
    localStorage.removeItem('edit-product');
    localStorage.removeItem('edit-product-index');
    localStorage.removeItem('edit-product-handle');
    localStorage.removeItem('edit-product-key');
  };

  if (!cart?.items?.length) {
    clearEditState();
    return;
  }

  const cartItem = cart.items.find((item) => item.handle === carthandle);
  if (!cartItem) {
    clearEditState();
    return;
  }

  localStorage.setItem('edit-product', cartItem.id);
  localStorage.setItem('edit-product-index', cart.items.indexOf(cartItem));
  localStorage.setItem('edit-product-handle', cartItem.handle);
  localStorage.setItem('edit-product-key', cartItem.key);
};

TwinkleCart.getProductSectionId = function () {
  return (
    document.querySelector('variant-radios, variant-selects')?.dataset.section ||
    document.querySelector('[id^="MainProduct-"]')?.dataset.section ||
    document.querySelector('product-form[data-section-id]')?.dataset.sectionId ||
    null
  );
};

TwinkleCart.getPdpHandle = function () {
  return window.location.pathname.match(/\/products\/([^/?#]+)/)?.[1] || null;
};

TwinkleCart.resetPdpAddButton = function () {
  const variantPicker = document.querySelector('variant-radios, variant-selects');
  if (variantPicker) {
    variantPicker.onVariantChange();
    return;
  }

  document.querySelectorAll('[name="add"], [name="update"]').forEach((button) => {
    if (button.getAttribute('name') === 'update') {
      button.setAttribute('name', 'add');
      button.removeAttribute('data-item-key');
    }

    const textSpan = button.querySelector('.add_to_cart-sold_out_btn') || button.querySelector('span');
    if (!textSpan || !window.variantStrings?.addToCart) return;

    const priceComponent = textSpan.querySelector('.price-component');
    const priceText = priceComponent?.textContent?.trim() || '';
    if (priceComponent && textSpan.childNodes[0]?.nodeType === 3) {
      textSpan.childNodes[0].textContent = window.variantStrings.addToCart + ' — ';
    } else if (priceText) {
      textSpan.innerHTML =
        window.variantStrings.addToCart + ' — <span class="price-component">' + priceText + '</span>';
    } else {
      textSpan.textContent = window.variantStrings.addToCart;
    }
  });

  const stickyATCText = document.querySelector('sticky-atc .product-sticky-atc .add_to_cart-sold_out_btn');
  if (stickyATCText && window.variantStrings?.addToCart) {
    const priceMatch = stickyATCText.textContent.match(/—\s*(.+)$/);
    stickyATCText.textContent =
      window.variantStrings.addToCart + (priceMatch ? ' — ' + priceMatch[1].trim() : '');
  }
};

TwinkleCart.refreshPdpAfterCartChange = async function (cart) {
  const carthandle = TwinkleCart.getPdpHandle();
  if (!carthandle || !cart) return;

  TwinkleCart.syncEditStateFromCart(cart);

  const inCart = cart.items?.some((item) => item.handle === carthandle);
  if (inCart) {
    document.querySelector('variant-radios, variant-selects')?.onVariantChange();
    return;
  }

  const sectionId = TwinkleCart.getProductSectionId();
  if (!sectionId) {
    TwinkleCart.resetPdpAddButton();
    return;
  }

  try {
    const response = await fetch(`${window.location.pathname}?sections=${sectionId}`).then((res) => res.json());
    const sectionHtml = response[sectionId];
    if (!sectionHtml || sectionHtml.includes('Liquid error')) {
      TwinkleCart.resetPdpAddButton();
      return;
    }

    const atcContainer = document.querySelector('#nd-main-product-button');
    if (atcContainer) {
      const buttonHtml = TwinkleCart.getSectionInnerHTML(sectionHtml, '#nd-main-product-button');
      if (buttonHtml) {
        atcContainer.innerHTML = buttonHtml;
        document.querySelector('product-form')?.syncSubmitButton?.();
        document.querySelector('variant-radios, variant-selects')?.onVariantChange();
        document.querySelector('localization-form')?.changeCity();
        return;
      }
    }

    const gridItem = TwinkleCart.getSectionInnerHTML(sectionHtml, '.product__info-wrapper.grid__item');
    const target = document.querySelector('.product__info-wrapper.grid__item');
    if (gridItem && target && gridItem.includes('product-form')) {
      target.innerHTML = gridItem;
      document.querySelector('variant-radios, variant-selects')?.onVariantChange();
      document.querySelector('localization-form')?.changeCity();
      return;
    }

    TwinkleCart.resetPdpAddButton();
  } catch (error) {
    console.error('TwinkleCart.refreshPdpAfterCartChange:', error);
    TwinkleCart.resetPdpAddButton();
  }
};

TwinkleCart.refreshFromServer = async function () {
  const drawer = TwinkleCart.getDrawer();
  const sectionIds = TwinkleCart.getDrawerSectionIds();
  if (!drawer || !sectionIds.length) return;

  try {
    const root = window.Shopify?.routes?.root || '/';
    const [cart, sections] = await Promise.all([
      fetch(`${root}cart.js`).then((response) => response.json()),
      fetch(`${root}?sections=${sectionIds.join(',')}`).then((response) => response.json()),
    ]);

    TwinkleCart.syncEditStateFromCart(cart);
    TwinkleCart.renderDrawerResponse({ ...cart, sections }, { openDrawer: false });
    TwinkleCart.dispatchCartUpdate(cart);
    TwinkleCart.refreshPdpAfterCartChange(cart);
  } catch (error) {
    console.error('TwinkleCart.refreshFromServer:', error);
  }
};

window.addEventListener('pageshow', (event) => {
  if (event.persisted) TwinkleCart.refreshFromServer();
});

TwinkleCart.afterCartUpdate = function () {
  setTimeout(() => {
    document.querySelector('variant-radios')?.onVariantChange();
  }, 500);
  document.querySelector('localization-form')?.changeCity();
};
